import { useEffect, useRef } from 'react';
import { globalEventEmitter } from '../utils/eventEmitter';
import type { DigilockerEventType, DigilockerEvent } from '../types/digilocker';

export const useEventEmitter = () => {
  const callbackRefs = useRef<Map<DigilockerEventType, (event: DigilockerEvent) => void>>(new Map());

  const on = (event: DigilockerEventType, callback: (event: DigilockerEvent) => void) => {
    // Store the callback reference for cleanup
    callbackRefs.current.set(event, callback);
    globalEventEmitter.on(event, callback);
  };

  const off = (event: DigilockerEventType) => {
    const callback = callbackRefs.current.get(event);
    if (callback) {
      globalEventEmitter.off(event, callback);
      callbackRefs.current.delete(event);
    }
  };

  const emit = (event: DigilockerEventType, data?: any) => {
    globalEventEmitter.emit(event, data);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      callbackRefs.current.forEach((callback, event) => {
        globalEventEmitter.off(event, callback);
      });
      callbackRefs.current.clear();
    };
  }, []);

  return { on, off, emit };
};

// Hook for listening to specific events
export const useEventListener = (
  event: DigilockerEventType,
  callback: (event: DigilockerEvent) => void,
  deps: any[] = []
) => {
  const { on, off } = useEventEmitter();

  useEffect(() => {
    on(event, callback);
    return () => off(event);
  }, [event, callback, ...deps]);
};
